import { useState, useRef, useCallback, useEffect } from "react";
import {
  Container,
  Stack,
  Box,
  Text,
  Heading,
  Divider,
  HStack,
  VStack,
  Card,
  CardBody,
  Button,
  Input,
  IconButton,
  useColorModeValue,
  Textarea,
  Badge,
  Flex,
  Alert,
  AlertIcon,
  AlertDescription,
  Progress,
  Spinner,
} from "@chakra-ui/react";
import {
  DeleteIcon,
  CheckIcon,
  CloseIcon,
  CheckCircleIcon,
  WarningIcon,
} from "@chakra-ui/icons";
import { motion } from "framer-motion";
import t from "../translations";

const MotionBox = motion(Box);
const color = "purple";

const ACCEPTED_TYPES = {
  "application/pdf": "pdf",
  "text/plain": "text",
  "application/msword": "docx",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "image/png": "image",
  "image/jpeg": "image",
  "image/gif": "image",
  "image/webp": "image",
};

const ACCEPT_STRING = Object.keys(ACCEPTED_TYPES).join(",");

function getFileCategory(file) {
  if (ACCEPTED_TYPES[file.type]) return ACCEPTED_TYPES[file.type];
  const ext = file.name.split(".").pop().toLowerCase();
  if (ext === "pdf") return "pdf";
  if (ext === "txt") return "text";
  if (["doc", "docx"].includes(ext)) return "docx";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "image";
  return null;
}

function FileTypeBadge({ category }) {
  const colorMap = { pdf: "red", text: "green", docx: "blue", image: "orange" };
  const labelMap = { pdf: "PDF", text: "TXT", docx: "DOCX", image: "IMG" };
  return (
    <Badge colorScheme={colorMap[category]} fontSize="xs" borderRadius="full">
      {labelMap[category]}
    </Badge>
  );
}

const TYPE_LABELS = {
  multipleChoice: "Multiple Choice",
  oneWord: "One Word",
  sentence: "Sentence",
};

function QuestionCard({ q, index, onAnswerChange, onCheck, isChecking }) {
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const correctBg = useColorModeValue("green.50", "rgba(72,187,120,0.1)");
  const incorrectBg = useColorModeValue("red.50", "rgba(245,101,101,0.1)");
  const optionBg = useColorModeValue("gray.50", "gray.700");
  const optionSelectedBg = useColorModeValue(`${color}.50`, "rgba(128,90,213,0.15)");
  const optionBorder = useColorModeValue("gray.200", "gray.600");

  const isMultipleChoice = q.type === "multipleChoice" && Array.isArray(q.options);

  const resultBg = q.isChecked
    ? q.isCorrect
      ? correctBg
      : incorrectBg
    : "transparent";

  const getOptionLetter = (option) => {
    const match = option.match(/^([A-D])\)/);
    return match ? match[1] : null;
  };

  const getOptionColor = (option) => {
    if (!q.isChecked) return {};
    const letter = getOptionLetter(option);
    if (letter === q.answer) return { bg: correctBg, borderColor: "green.400" };
    if (letter === q.userAnswer && !q.isCorrect) return { bg: incorrectBg, borderColor: "red.400" };
    return {};
  };

  return (
    <MotionBox
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
    >
      <Card
        bg={cardBg}
        borderRadius="xl"
        _hover={{ boxShadow: "xl" }}
        transition="box-shadow 0.25s ease"
        borderLeft="4px solid"
        borderLeftColor={
          q.isChecked
            ? q.isCorrect
              ? "green.400"
              : "red.400"
            : `${color}.400`
        }
      >
        <CardBody>
          <Stack spacing={4}>
            <HStack spacing={2}>
              <Badge colorScheme={color} fontSize="xs" borderRadius="full">
                Q{index + 1}
              </Badge>
              <Badge colorScheme="gray" fontSize="xs" borderRadius="full" variant="outline">
                {TYPE_LABELS[q.type] || q.type}
              </Badge>
            </HStack>

            <Text fontWeight={600} fontSize="md" lineHeight={1.8}>
              {q.question}
            </Text>

            {/* Multiple Choice Options */}
            {isMultipleChoice && (
              <Stack spacing={2}>
                {q.options.map((option) => {
                  const letter = getOptionLetter(option);
                  const isSelected = q.userAnswer === letter;
                  const colorOverrides = getOptionColor(option);

                  return (
                    <Box
                      key={option}
                      p={3}
                      borderRadius="lg"
                      border="2px solid"
                      borderColor={isSelected && !q.isChecked ? `${color}.400` : colorOverrides.borderColor || optionBorder}
                      bg={isSelected && !q.isChecked ? optionSelectedBg : colorOverrides.bg || optionBg}
                      cursor={q.isChecked ? "default" : "pointer"}
                      onClick={() => !q.isChecked && onAnswerChange(index, letter)}
                      transition="all 0.15s"
                      _hover={!q.isChecked ? { borderColor: `${color}.400`, bg: optionSelectedBg } : {}}
                    >
                      <Text fontSize="sm" fontWeight={isSelected ? 600 : 400}>
                        {option}
                      </Text>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {/* Text Input for oneWord / sentence */}
            {!isMultipleChoice && (
              <Textarea
                placeholder={
                  q.type === "oneWord"
                    ? "Type your answer (one word)..."
                    : t.forZahra.answerPlaceholder
                }
                value={q.userAnswer}
                onChange={(e) => onAnswerChange(index, e.target.value)}
                isDisabled={q.isChecked}
                size="sm"
                borderRadius="lg"
                rows={q.type === "oneWord" ? 1 : 3}
                focusBorderColor={`${color}.400`}
                _placeholder={{ color: "gray.500" }}
              />
            )}

            {!q.isChecked && (
              <Button
                size="sm"
                colorScheme={color}
                onClick={() => onCheck(index)}
                isLoading={isChecking}
                loadingText={t.forZahra.checking}
                isDisabled={!q.userAnswer || !q.userAnswer.trim()}
                borderRadius="full"
                alignSelf="flex-start"
              >
                {t.forZahra.checkAnswer}
              </Button>
            )}

            {q.isChecked && (
              <Box bg={resultBg} p={4} borderRadius="lg">
                <HStack mb={2}>
                  {q.isCorrect ? (
                    <Badge colorScheme="green" fontSize="sm" borderRadius="full" px={3} py={1}>
                      <HStack spacing={1}>
                        <CheckIcon boxSize={3} />
                        <Text>{t.forZahra.correct}</Text>
                      </HStack>
                    </Badge>
                  ) : (
                    <Badge colorScheme="red" fontSize="sm" borderRadius="full" px={3} py={1}>
                      <HStack spacing={1}>
                        <CloseIcon boxSize={3} />
                        <Text>{t.forZahra.incorrect}</Text>
                      </HStack>
                    </Badge>
                  )}
                </HStack>

                {q.feedback && (
                  <Text color={textColor} fontSize="sm" mb={2}>
                    {q.feedback}
                  </Text>
                )}

                <Box>
                  <Text fontWeight={600} fontSize="sm" color={`${color}.400`} mb={1}>
                    {t.forZahra.modelAnswer}
                  </Text>
                  <Text color={textColor} fontSize="sm" lineHeight={1.8}>
                    {isMultipleChoice
                      ? q.options.find((o) => o.startsWith(q.answer + ")")) || q.answer
                      : q.answer}
                  </Text>
                </Box>
              </Box>
            )}
          </Stack>
        </CardBody>
      </Card>
    </MotionBox>
  );
}

export default function ForZahra() {
  const [serverStatus, setServerStatus] = useState("checking");

  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [questionTypes, setQuestionTypes] = useState(["multipleChoice"]);
  const [questionCount, setQuestionCount] = useState(10);
  const [difficulty, setDifficulty] = useState("medium");
  const [lastFormData, setLastFormData] = useState(null);
  const [previousQuestions, setPreviousQuestions] = useState([]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [generateError, setGenerateError] = useState("");
  const [checkingIndex, setCheckingIndex] = useState(null);

  const fileInputRef = useRef(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const dropzoneBg = useColorModeValue("gray.50", "gray.700");
  const dropzoneBorder = useColorModeValue("gray.300", "gray.500");
  const dropzoneActiveBg = useColorModeValue(
    `${color}.50`,
    "rgba(128,90,213,0.1)"
  );

  useEffect(() => {
    document.title = t.forZahra.pageTitle;
  }, []);

  const checkServer = useCallback(async () => {
    setServerStatus("checking");
    try {
      const res = await fetch("/api/health", { signal: AbortSignal.timeout(5000) });
      setServerStatus(res.ok ? "online" : "offline");
    } catch {
      setServerStatus("offline");
    }
  }, []);

  useEffect(() => {
    checkServer();
  }, [checkServer]);

  const addFiles = useCallback((newFiles) => {
    const validFiles = Array.from(newFiles)
      .map((file) => {
        const category = getFileCategory(file);
        if (!category) return null;
        return { file, name: file.name, category, id: Math.random().toString(36).slice(2) };
      })
      .filter(Boolean);

    setFiles((prev) => [...prev, ...validFiles]);
  }, []);

  const removeFile = useCallback((id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const handleGenerateQuestions = useCallback(async () => {
    if (files.length === 0) {
      setGenerateError(t.forZahra.noFiles);
      return;
    }

    setIsGenerating(true);
    setGenerateError("");
    setQuestions([]);

    try {
      const formData = new FormData();
      for (const f of files) {
        formData.append("files", f.file);
      }
      formData.append("questionTypes", JSON.stringify(questionTypes));
      formData.append("questionCount", questionCount);
      formData.append("difficulty", difficulty);
      if (previousQuestions.length > 0) {
        formData.append("previousQuestions", JSON.stringify(previousQuestions));
      }

      setLastFormData({ files: [...files], questionTypes: [...questionTypes], questionCount, difficulty });

      const response = await fetch("/api/generate", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error (${response.status})`);
      }

      const data = await response.json();
      const responseText = data.response;

      const jsonMatch = responseText.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error(t.forZahra.parseError);

      const parsed = JSON.parse(jsonMatch[0]);
      setPreviousQuestions((prev) => [...prev, ...parsed.map((q) => q.question)]);
      setQuestions(
        parsed.map((q) => ({
          ...q,
          userAnswer: "",
          isChecked: false,
          isCorrect: null,
          feedback: "",
        }))
      );
    } catch (err) {
      setGenerateError(err.message);
    } finally {
      setIsGenerating(false);
    }
  }, [files, questionTypes, questionCount, difficulty, previousQuestions]);

  const handleAnswerChange = useCallback((index, value) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, userAnswer: value } : q))
    );
  }, []);

  const handleCheckAnswer = useCallback(
    async (index) => {
      const q = questions[index];
      setCheckingIndex(index);

      // Multiple choice: check locally
      if (q.type === "multipleChoice") {
        const isCorrect = q.userAnswer.toUpperCase() === q.answer.toUpperCase();
        setQuestions((prev) =>
          prev.map((qq, i) =>
            i === index
              ? {
                  ...qq,
                  isChecked: true,
                  isCorrect,
                  feedback: isCorrect ? "Well done!" : "That wasn't quite right.",
                }
              : qq
          )
        );
        setCheckingIndex(null);
        return;
      }

      try {
        const response = await fetch("/api/check-answer", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            question: q.question,
            answer: q.answer,
            userAnswer: q.userAnswer,
          }),
        });

        if (!response.ok) throw new Error("Failed to check answer");

        const data = await response.json();
        const result = JSON.parse(data.response.match(/\{[\s\S]*\}/)[0]);

        setQuestions((prev) =>
          prev.map((qq, i) =>
            i === index
              ? {
                  ...qq,
                  isChecked: true,
                  isCorrect: result.correct,
                  feedback: result.feedback,
                }
              : qq
          )
        );
      } catch {
        setQuestions((prev) =>
          prev.map((qq, i) =>
            i === index
              ? {
                  ...qq,
                  isChecked: true,
                  isCorrect: false,
                  feedback: t.forZahra.checkFallback,
                }
              : qq
          )
        );
      } finally {
        setCheckingIndex(null);
      }
    },
    [questions]
  );

  const toggleQuestionType = useCallback((type) => {
    setQuestionTypes((prev) => {
      if (prev.includes(type)) {
        if (prev.length === 1) return prev;
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  }, []);

  const handleGenerateMore = useCallback(async () => {
    if (!lastFormData) return;

    setIsGenerating(true);
    setGenerateError("");
    setQuestions([]);

    try {
      const formData = new FormData();
      for (const f of lastFormData.files) {
        formData.append("files", f.file);
      }
      formData.append("questionTypes", JSON.stringify(lastFormData.questionTypes));
      formData.append("questionCount", lastFormData.questionCount);
      formData.append("difficulty", lastFormData.difficulty);
      if (previousQuestions.length > 0) {
        formData.append("previousQuestions", JSON.stringify(previousQuestions));
      }

      const response = await fetch("/api/generate", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error (${response.status})`);
      }

      const data = await response.json();
      const responseText = data.response;

      const jsonMatch = responseText.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error(t.forZahra.parseError);

      const parsed = JSON.parse(jsonMatch[0]);
      setPreviousQuestions((prev) => [...prev, ...parsed.map((q) => q.question)]);
      setQuestions(
        parsed.map((q) => ({
          ...q,
          userAnswer: "",
          isChecked: false,
          isCorrect: null,
          feedback: "",
        }))
      );
    } catch (err) {
      setGenerateError(err.message);
    } finally {
      setIsGenerating(false);
    }
  }, [lastFormData, previousQuestions]);

  const handleReset = useCallback(() => {
    setQuestions([]);
    setFiles([]);
    setGenerateError("");
    setLastFormData(null);
    setPreviousQuestions([]);
  }, []);

  // --- MAIN VIEW ---
  return (
    <Container maxW="4xl" pt={{ base: 20, md: 28 }} pb={{ base: 20, md: 36 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <Stack align="center" direction="row" px={4}>
          <HStack mx={4} spacing={3}>
            <Text fontWeight={800} fontSize={{ base: "xl", md: "2xl" }} whiteSpace="nowrap">
              {t.forZahra.heading}
            </Text>
            {serverStatus === "checking" && (
              <Badge colorScheme="yellow" borderRadius="full" px={3} py={1}>
                <HStack spacing={1}>
                  <Spinner size="xs" />
                  <Text fontSize="xs">Checking...</Text>
                </HStack>
              </Badge>
            )}
            {serverStatus === "online" && (
              <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
                <HStack spacing={1}>
                  <CheckCircleIcon boxSize={3} />
                  <Text fontSize="xs">Server Online</Text>
                </HStack>
              </Badge>
            )}
            {serverStatus === "offline" && (
              <Badge colorScheme="red" borderRadius="full" px={3} py={1} cursor="pointer" onClick={checkServer}>
                <HStack spacing={1}>
                  <WarningIcon boxSize={3} />
                  <Text fontSize="xs">Server Offline</Text>
                </HStack>
              </Badge>
            )}
          </HStack>
          <Divider />
        </Stack>

        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          px={4}
        >
          <Text color={textColor} fontSize="lg" lineHeight={1.9}>
            {t.forZahra.intro}
          </Text>
        </MotionBox>

        {/* File Upload Area */}
        {questions.length === 0 && (
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            px={4}
          >
            <Stack spacing={4}>
              {/* Dropzone */}
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                cursor="pointer"
                border="2px dashed"
                borderColor={isDragging ? `${color}.400` : dropzoneBorder}
                bg={isDragging ? dropzoneActiveBg : dropzoneBg}
                borderRadius="xl"
                p={{ base: 8, md: 12 }}
                textAlign="center"
                transition="all 0.2s"
                _hover={{
                  borderColor: `${color}.400`,
                  bg: dropzoneActiveBg,
                }}
              >
                <VStack spacing={3}>
                  <Text fontSize="3xl">
                    {isDragging ? "\u{1F4E5}" : "\u{1F4C4}"}
                  </Text>
                  <Text fontWeight={600} fontSize="md">
                    {isDragging
                      ? t.forZahra.dropHere
                      : t.forZahra.uploadPrompt}
                  </Text>
                  <Text color={textColor} fontSize="sm">
                    {t.forZahra.supportedFormats}
                  </Text>
                </VStack>

                <Input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_STRING}
                  multiple
                  onChange={(e) => addFiles(e.target.files)}
                  display="none"
                />
              </Box>

              {/* Uploaded Files */}
              {files.length > 0 && (
                <Stack spacing={2}>
                  <Text fontWeight={600} fontSize="sm" color={`${color}.400`}>
                    {t.forZahra.uploadedFiles} ({files.length})
                  </Text>
                  {files.map((f) => (
                    <Card key={f.id} bg={cardBg} borderRadius="lg" size="sm">
                      <CardBody py={2} px={4}>
                        <HStack justify="space-between">
                          <HStack spacing={3}>
                            <FileTypeBadge category={f.category} />
                            <Text fontSize="sm" noOfLines={1}>
                              {f.name}
                            </Text>
                          </HStack>
                          <IconButton
                            icon={<DeleteIcon />}
                            size="xs"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeFile(f.id)}
                            aria-label="Remove file"
                          />
                        </HStack>
                      </CardBody>
                    </Card>
                  ))}
                </Stack>
              )}

              {/* Question Type Selection */}
              <Stack spacing={3}>
                <Text fontWeight={600} fontSize="sm" color={`${color}.400`}>
                  Question Types
                </Text>
                <HStack spacing={3} flexWrap="wrap">
                  {[
                    { key: "multipleChoice", label: "Multiple Choice" },
                    { key: "oneWord", label: "One Word Answer" },
                    { key: "sentence", label: "Sentence Answer" },
                  ].map(({ key, label }) => {
                    const selected = questionTypes.includes(key);
                    return (
                      <Button
                        key={key}
                        size="sm"
                        borderRadius="full"
                        variant={selected ? "solid" : "outline"}
                        colorScheme={selected ? color : "gray"}
                        onClick={() => toggleQuestionType(key)}
                        boxShadow={selected ? `0 0 12px 2px var(--chakra-colors-${color}-400)` : "none"}
                        transition="all 0.2s"
                      >
                        {label}
                      </Button>
                    );
                  })}
                </HStack>
                <Text color={textColor} fontSize="xs">
                  Select one or more question types. At least one must be selected.
                </Text>
              </Stack>

              {/* Question Count */}
              <Stack spacing={3}>
                <Text fontWeight={600} fontSize="sm" color={`${color}.400`}>
                  Number of Questions
                </Text>
                <HStack spacing={3} flexWrap="wrap">
                  {[5, 10, 15, 20, 30].map((count) => (
                    <Button
                      key={count}
                      size="sm"
                      borderRadius="full"
                      variant={questionCount === count ? "solid" : "outline"}
                      colorScheme={questionCount === count ? color : "gray"}
                      onClick={() => setQuestionCount(count)}
                      boxShadow={questionCount === count ? `0 0 12px 2px var(--chakra-colors-${color}-400)` : "none"}
                      transition="all 0.2s"
                    >
                      {count}
                    </Button>
                  ))}
                </HStack>
              </Stack>

              {/* Difficulty Level */}
              <Stack spacing={3}>
                <Text fontWeight={600} fontSize="sm" color={`${color}.400`}>
                  Difficulty Level
                </Text>
                <HStack spacing={3} flexWrap="wrap">
                  {[
                    { key: "easy", label: "Easy", scheme: "green" },
                    { key: "medium", label: "Medium", scheme: "yellow" },
                    { key: "hard", label: "Hard", scheme: "red" },
                  ].map(({ key, label, scheme }) => {
                    const selected = difficulty === key;
                    return (
                      <Button
                        key={key}
                        size="sm"
                        borderRadius="full"
                        variant={selected ? "solid" : "outline"}
                        colorScheme={selected ? scheme : "gray"}
                        onClick={() => setDifficulty(key)}
                        boxShadow={selected ? `0 0 12px 2px var(--chakra-colors-${scheme}-400)` : "none"}
                        transition="all 0.2s"
                      >
                        {label}
                      </Button>
                    );
                  })}
                </HStack>
              </Stack>

              {/* Generate Button */}
              <Button
                colorScheme={color}
                size="lg"
                borderRadius="full"
                onClick={handleGenerateQuestions}
                isLoading={isGenerating}
                loadingText={t.forZahra.generating}
                isDisabled={files.length === 0}
                leftIcon={<Text fontSize="lg">{"\u2728"}</Text>}
                boxShadow={`0 0 20px 4px var(--chakra-colors-${color}-400)`}
                _hover={{
                  boxShadow: `0 0 28px 6px var(--chakra-colors-${color}-400)`,
                  transform: "translateY(-2px)",
                }}
                transition="all 0.2s"
              >
                {t.forZahra.generateButton}
              </Button>

              {isGenerating && (
                <VStack spacing={3}>
                  <Progress
                    size="xs"
                    isIndeterminate
                    colorScheme={color}
                    borderRadius="full"
                    w="100%"
                  />
                  <Text color={textColor} fontSize="sm">
                    {t.forZahra.generatingHint}
                  </Text>
                </VStack>
              )}
            </Stack>
          </MotionBox>
        )}

        {/* Error */}
        {generateError && (
          <Box px={4}>
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              <AlertDescription fontSize="sm">{generateError}</AlertDescription>
            </Alert>
          </Box>
        )}

        {/* Questions */}
        {questions.length > 0 && (
          <MotionBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            px={4}
          >
            <Stack spacing={6}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <HStack spacing={3}>
                  <Heading size="md">{t.forZahra.questionsTitle}</Heading>
                  <Badge colorScheme={color} borderRadius="full" px={3} py={1}>
                    {questions.length} {t.forZahra.questionsCount}
                  </Badge>
                </HStack>

                <HStack spacing={2}>
                  {questions.some((q) => q.isChecked) && (
                    <HStack spacing={2}>
                      <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
                        {questions.filter((q) => q.isCorrect).length} {t.forZahra.correctLabel}
                      </Badge>
                      <Badge colorScheme="red" borderRadius="full" px={3} py={1}>
                        {questions.filter((q) => q.isChecked && !q.isCorrect).length}{" "}
                        {t.forZahra.incorrectLabel}
                      </Badge>
                    </HStack>
                  )}
                  <Button
                    size="sm"
                    colorScheme={color}
                    borderRadius="full"
                    onClick={handleGenerateMore}
                    isLoading={isGenerating}
                    loadingText="Generating..."
                    isDisabled={!lastFormData}
                  >
                    Generate More Questions
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme={color}
                    borderRadius="full"
                    onClick={handleReset}
                  >
                    {t.forZahra.newSession}
                  </Button>
                </HStack>
              </HStack>

              <Stack spacing={4}>
                {questions.map((q, i) => (
                  <QuestionCard
                    key={i}
                    q={q}
                    index={i}
                    onAnswerChange={handleAnswerChange}
                    onCheck={handleCheckAnswer}
                    isChecking={checkingIndex === i}
                  />
                ))}
              </Stack>
            </Stack>
          </MotionBox>
        )}
      </Stack>
    </Container>
  );
}
